import { TFunction } from 'i18next';
import systemInfo from 'src/js/data/systemInfo';
import {
  convertKBytesToSize,
  convertRecordSizeToKBytes,
  FecRTP,
  MpegProFecLevel,
  RecordLimitType,
  SRTMode,
  SrtPathRedundancyMode,
  StreamEncapsulation,
  StreamShaping,
} from 'src/js/pages/streaming/stream/stream-models';
import { isEvenNumber } from 'src/js/validator/number-validator-helper';
import { autoAssignOrNumberOrEmpty, ipOrHostname, url } from 'src/js/validator/validator';
import * as yup from 'yup';

/*
 * Schema for the form
 */
const regExpYaml = /^[^,]*$/; // no commas
const portNumber = (t: TFunction) =>
  yup
    .number()
    .required(t('validation.required'))
    .typeError(t('validation.stream.portNumber'))
    .min(1, t('validation.stream.portNumber'))
    .max(65535, t('validation.stream.portNumber'));

// getTotalRecordingBitrateInKbps() returns combined video + first audio encoder bitrate (Kbps) currently selected on the stream form,
// used to compute the minimum recording/segment size, matching _CheckForMinRecordingLength() logic
export const streamValidationSchema = (t: TFunction, getTotalRecordingBitrateInKbps: () => number) =>
  yup.object().shape({
    name: yup.string().max(63, t('validation.maxLength', { length: 63 })),
    protocol: yup.string(),
    bearerToken: yup.string().when(['encapsulation'], {
      is: (encapsulation) => {
        if (encapsulation === StreamEncapsulation.WHIP) {
          return true;
        }
        return false;
      },
      then: yup.string().max(4096, t('validation.stream.bearerToken')),
    }),
    url: yup.string().when(['encapsulation'], {
      is: (encapsulation) => {
        if (encapsulation === StreamEncapsulation.WHIP) {
          return true;
        }
        return false;
      },
      then: url(t),
    }),
    address: yup
      .string()
      .when(['encapsulation', 'srtMode'], {
        is: (encapsulation, srtMode) => {
          if (
            encapsulation === StreamEncapsulation.SRT &&
            (srtMode === SRTMode.RENDEZVOUS || srtMode === SRTMode.CALLER)
          ) {
            return true;
          }
          return false;
        },
        then: ipOrHostname(t),
      })
      .when(['encapsulation'], {
        is: (encapsulation) => {
          if (encapsulation === StreamEncapsulation.UDP || encapsulation === StreamEncapsulation.RTP) {
            return true;
          }
          return false;
        },
        then: ipOrHostname(t),
      }),
    srtRedundancyPath2Address: yup.string().when(['encapsulation', 'srtMode', 'srtRedundancyMode'], {
      is: (encapsulation, srtMode, srtRedundancyMode) => {
        if (
          encapsulation === StreamEncapsulation.SRT &&
          srtMode === SRTMode.CALLER &&
          (srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
            srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP)
        ) {
          return true;
        }
        return false;
      },
      then: ipOrHostname(t),
    }),
    srtRedundancyPath2Port: yup.mixed().when(['encapsulation', 'srtMode', 'srtRedundancyMode'], {
      is: (encapsulation, srtMode, srtRedundancyMode) => {
        if (
          encapsulation === StreamEncapsulation.SRT &&
          srtMode === SRTMode.CALLER &&
          (srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
            srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP)
        ) {
          return true;
        }
        return false;
      },
      then: portNumber(t),
    }),
    encapsulation: yup.mixed().oneOf([2, 3, 34, 64, 128], t('validation.stream.encapsulation')),
    overheadPercentage: yup
      .number()
      .when(['encapsulation', 'shaping'], {
        is: (encapsulation) => {
          if (encapsulation === StreamEncapsulation.SRT) {
            return true;
          }
          return false;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.overheadPercentageSrt'))
          .min(5, t('validation.stream.overheadPercentageSrt'))
          .max(50, t('validation.stream.overheadPercentageSrt')),
      })
      .when(['encapsulation', 'shaping'], {
        is: (encapsulation, shaping) => {
          if (encapsulation !== StreamEncapsulation.SRT && shaping !== StreamShaping.VBR) {
            return true;
          }
          return false;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.overheadPercentage'))
          .min(5, t('validation.stream.overheadPercentage'))
          .max(100, t('validation.stream.overheadPercentage')),
      }),
    fecRtp: yup.mixed().oneOf([FecRTP.DISABLED, FecRTP.MPEG_PRO_FEC], t('validation.stream.fecMode')),
    level: yup.mixed().when('fecRtp', {
      is: (fecRtp) => fecRtp === FecRTP.MPEG_PRO_FEC,
      then: yup.number().oneOf([MpegProFecLevel.COLUMNS_ONLY, MpegProFecLevel.ROW_AND_COLUMNS]),
    }),
    rows: yup.mixed().when(['fecRtp', 'level'], {
      is: (fecRtp, level) => {
        return fecRtp === FecRTP.MPEG_PRO_FEC && level === MpegProFecLevel.ROW_AND_COLUMNS;
      },
      then: yup
        .number()
        .required(t('validation.required'))
        .typeError(t('validation.stream.rows'))
        .min(4, t('validation.stream.rows'))
        .max(20, t('validation.stream.rows')),
    }),
    columns: yup
      .mixed()
      .when(['fecRtp', 'level'], {
        is: (fecRtp, level) => {
          const checkColumns = fecRtp === FecRTP.MPEG_PRO_FEC && level === MpegProFecLevel.COLUMNS_ONLY;
          return checkColumns;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.columnsA'))
          .min(1, t('validation.stream.columnsA'))
          .max(20, t('validation.stream.columnsA')),
      })
      .when(['fecRtp', 'level'], {
        is: (fecRtp, level) => {
          const checkColumns = fecRtp === FecRTP.MPEG_PRO_FEC && level === MpegProFecLevel.ROW_AND_COLUMNS;
          return checkColumns;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.columnsB'))
          .min(4, t('validation.stream.columnsB'))
          .max(20, t('validation.stream.columnsB'))
          .test('rowsColsMixed', t('validation.stream.rowsColsMixed'), function (columns) {
            if (columns * this.parent.rows > 100) {
              return false;
            }
            return true;
          }),
      }),
    // SRT
    srtMode: yup.mixed().oneOf([0, 1, 2], t('validation.stream.srtMode')),
    sourcePort: autoAssignOrNumberOrEmpty(t),
    port: yup.mixed().when('encapsulation', {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: portNumber(t).when('encapsulation', {
        is: StreamEncapsulation.RTP,
        then: portNumber(t).test('is-even', t('validation.stream.portNumberRTPError'), isEvenNumber),
      }),
    }),
    latency: yup.number().when('encapsulation', {
      is: StreamEncapsulation.SRT,
      then: yup
        .number()
        .required(t('validation.required'))
        .typeError(t('validation.stream.latencyNumber'))
        .min(20, t('validation.stream.latencyNumber'))
        .max(8000, t('validation.stream.latencyNumber')),
    }),
    encrypted: yup.boolean(),
    passphrase: yup
      .string()
      .when(['encapsulation', 'encKeyLength', 'passphraseSet'], {
        is: (encapsulation, encKeyLength, passphraseSet) =>
          encapsulation == StreamEncapsulation.SRT && encKeyLength > 0 && !passphraseSet,
        then: systemInfo.isFipsEnabled
          ? yup
              .string()
              .required(t('validation.required'))
              .min(14, t('validation.stream.maxPassphraseLengthFips'))
              .max(79, t('validation.stream.maxPassphraseLengthFips'))
          : yup
              .string()
              .required(t('validation.required'))
              .min(10, t('validation.stream.maxPassphraseLength'))
              .max(79, t('validation.stream.maxPassphraseLength')),
      })
      .when(['encapsulation', 'encKeyLength', 'passphraseSet'], {
        is: (encapsulation, encKeyLength, passphraseSet) =>
          encapsulation == StreamEncapsulation.SRT && encKeyLength > 0 && passphraseSet,
        then: systemInfo.isFipsEnabled
          ? yup
              .string()
              .min(14, t('validation.stream.maxPassphraseLengthFips'))
              .max(79, t('validation.stream.maxPassphraseLengthFips'))
          : yup
              .string()
              .min(10, t('validation.stream.maxPassphraseLength'))
              .max(79, t('validation.stream.maxPassphraseLength')),
      }),
    srtAccessUserName: yup.string().when(['srtStreamId', 'srtAccessControlPublishingIdType'], {
      is: (srtStreamId, srtAccessControlPublishingIdType) => {
        if (srtAccessControlPublishingIdType === 'custom') {
          return false;
        }
        if (srtStreamId) {
          return true;
        }
        return false;
      },
      then: yup.string().matches(regExpYaml, t('validation.stream.noCommas')),
    }),
    srtAccessResourceName: yup.string().when(['srtStreamId', 'srtAccessControlPublishingIdType'], {
      is: (srtStreamId, srtAccessControlPublishingIdType) => {
        if (srtAccessControlPublishingIdType === 'custom') {
          return false;
        }
        if (srtStreamId) {
          return true;
        }
        return false;
      },
      then: yup.string().matches(regExpYaml, t('validation.stream.noCommas')),
    }),
    mtu: yup
      .number()
      .typeError(t('validation.stream.mtu'))
      .min(268, t('validation.stream.mtu'))
      .max(1500, t('validation.stream.mtu')),
    tos: yup
      .number()
      .typeError(t('validation.stream.tos'))
      .min(0, t('validation.stream.tos'))
      .max(255, t('validation.stream.tos')),
    ttl: yup
      .number()
      .typeError(t('validation.stream.ttl'))
      .min(1, t('validation.stream.ttl'))
      .max(255, t('validation.stream.ttl')),
    sap: yup.object().when('encapsulation', {
      is: (encapsulation) => encapsulation === StreamEncapsulation.UDP || encapsulation === StreamEncapsulation.RTP,
      then: yup.object().shape({
        name: yup.string().when('advertise', {
          is: (val) => {
            // console.log('what is advertise?', val);
            return val;
          },
          then: yup
            .string()
            .required(t('validation.required'))
            .max(63, t('validation.maxLength', { length: 63 })),
        }),
      }),
    }),
    videoPids: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.videoPids'))
        .min(16, t('validation.stream.videoPids'))
        .max(8190, t('validation.stream.videoPids')),
    }),
    audioPids: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup.mixed().test('is-pid-array', t('validation.stream.audioPids'), (pids) => {
        const audioPids = typeof pids === 'string' ? pids.trim().split(',') : [];
        for (let i = 0; i < audioPids.length; i++) {
          if (/\D/.test(audioPids[i].trim())) {
            return false;
          } else if (Number(audioPids[i].trim()) < 16 || Number(audioPids[i].trim()) > 8190) {
            return false;
          }
        }
        return true;
      }),
    }),
    metadataPids: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.metadataPids'))
        .min(16, t('validation.stream.metadataPids'))
        .max(8190, t('validation.stream.metadataPids')),
    }),
    sctePids: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.sctePids'))
        .min(16, t('validation.stream.sctePids'))
        .max(8190, t('validation.stream.sctePids')),
    }),
    pcrPid: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.pcrPid'))
        .min(16, t('validation.stream.pcrPid'))
        .max(8190, t('validation.stream.pcrPid')),
    }),
    pmtPid: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.pmtPid'))
        .min(16, t('validation.stream.pmtPid'))
        .max(8190, t('validation.stream.pmtPid')),
    }),
    tsStreamId: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.tsStreamId'))
        .min(0, t('validation.stream.tsStreamId'))
        .max(65535, t('validation.stream.tsStreamId')),
    }),
    programNumber: yup.mixed().when(['encapsulation'], {
      is: (encapsulation) => encapsulation !== StreamEncapsulation.WHIP,
      then: yup
        .number()
        .typeError(t('validation.stream.programNumber'))
        .min(1, t('validation.stream.programNumber'))
        .max(65535, t('validation.stream.programNumber')),
    }),
    recording: yup.object().shape({
      name: yup.string().when('enabled', {
        is: true,
        then: yup.string().max(63, t('validation.maxLength', { length: 63 })),
      }),
      limitSizeHour: yup.mixed().when(['enabled', 'limitType'], {
        is: (enabled, limitType) => enabled === true && limitType === RecordLimitType.TIME,
        then: yup
          .number()
          .typeError(t('validation.recording.limitSizeHour'))
          .min(0, t('validation.recording.limitSizeHour'))
          .max(24, t('validation.recording.limitSizeHour'))
          .test('totalLimitSizeExceeded', t('validation.recording.totalLimitSizeExceeded'), function (limitSizeHour) {
            const limitSizeMinutes = this.parent.limitSizeMinutes;
            return (limitSizeHour ?? 0) + (limitSizeMinutes ?? 0) / 60 <= 24;
          }),
      }),
      limitSizeMinutes: yup.mixed().when(['enabled', 'limitType'], {
        is: (enabled, limitType) => enabled === true && limitType === RecordLimitType.TIME,
        then: yup
          .number()
          .typeError(t('validation.recording.limitSizeMinutes'))
          .min(0, t('validation.recording.limitSizeMinutes'))
          .max(1440, t('validation.recording.limitSizeMinutes'))
          .test(
            'totalLimitSizeExceeded',
            t('validation.recording.totalLimitSizeExceeded'),
            function (limitSizeMinutes) {
              const limitSizeHour = this.parent.limitSizeHour;
              return (limitSizeHour ?? 0) + (limitSizeMinutes ?? 0) / 60 <= 24;
            },
          ),
      }),
      segmentHour: yup.mixed().when(['enabled', 'segmentType'], {
        is: (enabled, segmentType) => enabled === true && segmentType === RecordLimitType.TIME,
        then: yup
          .number()
          .typeError(t('validation.recording.limitSizeHour'))
          .min(0, t('validation.recording.limitSizeHour'))
          .max(24, t('validation.recording.limitSizeHour'))
          .test('totalSegmentExceeded', t('validation.recording.totalSegmentExceeded'), function (segmentHour) {
            const segmentMinutes = this.parent.segmentMinutes;
            return (segmentHour ?? 0) + (segmentMinutes ?? 0) / 60 <= 24;
          }),
      }),
      segmentMinutes: yup.mixed().when(['enabled', 'segmentType'], {
        is: (enabled, segmentType) => enabled === true && segmentType === RecordLimitType.TIME,
        then: yup
          .number()
          .typeError(t('validation.recording.limitSizeMinutes'))
          .min(0, t('validation.recording.limitSizeMinutes'))
          .max(1440, t('validation.recording.limitSizeMinutes'))
          .test('totalSegmentExceeded', t('validation.recording.totalSegmentExceeded'), function (segmentMinutes) {
            const segmentHour = this.parent.segmentHour;
            return (segmentHour ?? 0) + (segmentMinutes ?? 0) / 60 <= 24;
          }),
      }),
      limitSize: yup.mixed().when(['enabled', 'limitType'], {
        is: (enabled, limitType) => enabled === true && limitType === RecordLimitType.SIZE,
        then: yup
          .number()
          .typeError(t('validation.recording.limitSize'))
          .min(1, t('validation.recording.limitSize'))
          .test('maxRecordingSizeExceeded', '', function (limitSize) {
            // cap at 100GB regardless of the selected unit (GB/MB)
            const maxSizeInKBytes = 100 * 1024 * 1024;
            const limitSizeInKBytes = convertRecordSizeToKBytes(limitSize, this.parent.limitSizeUnit);
            if (limitSizeInKBytes <= maxSizeInKBytes) {
              return true;
            }
            return this.createError({
              message: t('validation.recording.limitSizeMax', {
                max: convertKBytesToSize(maxSizeInKBytes, this.parent.limitSizeUnit),
                unit: this.parent.limitSizeUnit,
              }),
            });
          })
          .test('minRecordingSize', '', function (limitSize) {
            // matches C _CheckForMinRecordingLength: minSize = (totalBitrateInKbps * 60) / 8 KBytes (1 minute of recording)
            const totalBitrateInKbps = getTotalRecordingBitrateInKbps();
            if (!totalBitrateInKbps) {
              return true; // no encoder selected yet, nothing to validate against
            }
            const minSizeInKBytes = Math.floor((totalBitrateInKbps * 60) / 8);
            const limitSizeInKBytes = convertRecordSizeToKBytes(limitSize, this.parent.limitSizeUnit);
            if (limitSizeInKBytes >= minSizeInKBytes) {
              return true;
            }
            return this.createError({
              message: t('validation.recording.limitSizeTooSmall', {
                min: convertKBytesToSize(minSizeInKBytes, this.parent.limitSizeUnit),
                unit: this.parent.limitSizeUnit,
              }),
            });
          }),
      }),
      segmentSize: yup.mixed().when(['enabled', 'segmentType'], {
        is: (enabled, segmentType) => enabled === true && segmentType === RecordLimitType.SIZE,
        then: yup
          .number()
          .typeError(t('validation.recording.segmentSize'))
          .min(1, t('validation.recording.segmentSize'))
          .test('maxRecordingSizeExceeded', '', function (segmentSize) {
            // cap at 100GB regardless of the selected unit (GB/MB)
            const maxSizeInKBytes = 100 * 1024 * 1024;
            const segmentSizeInKBytes = convertRecordSizeToKBytes(segmentSize, this.parent.segmentSizeUnit);
            if (segmentSizeInKBytes <= maxSizeInKBytes) {
              return true;
            }
            return this.createError({
              message: t('validation.recording.limitSizeMax', {
                max: convertKBytesToSize(maxSizeInKBytes, this.parent.segmentSizeUnit),
                unit: this.parent.segmentSizeUnit,
              }),
            });
          })
          .test('minRecordingSize', '', function (segmentSize) {
            const totalBitrateInKbps = getTotalRecordingBitrateInKbps();
            if (!totalBitrateInKbps) {
              return true; // no encoder selected yet, nothing to validate against
            }
            const minSizeInKBytes = Math.floor((totalBitrateInKbps * 60) / 8);
            const segmentSizeInKBytes = convertRecordSizeToKBytes(segmentSize, this.parent.segmentSizeUnit);
            if (segmentSizeInKBytes >= minSizeInKBytes) {
              return true;
            }
            return this.createError({
              message: t('validation.recording.segmentSizeTooSmall', {
                min: convertKBytesToSize(minSizeInKBytes, this.parent.segmentSizeUnit),
                unit: this.parent.segmentSizeUnit,
              }),
            });
          })
          .test(
            'segmentSmallerThanLimit',
            t('validation.recording.segmentMustBeSmallerThanLimit'),
            function (segmentSize) {
              if (this.parent.limitType !== RecordLimitType.SIZE) {
                return true; // only comparable when overall limit is also size based
              }
              const segmentSizeInKBytes = convertRecordSizeToKBytes(segmentSize, this.parent.segmentSizeUnit);
              const limitSizeInKBytes = convertRecordSizeToKBytes(this.parent.limitSize, this.parent.limitSizeUnit);
              return segmentSizeInKBytes < limitSizeInKBytes;
            },
          ),
      }),
    }),
  });
